import {useCallback, useEffect, useRef} from "react";
import {getUserInfo} from "@/api/UserApi.ts";

const KEEP_ALIVE_INTERVAL_MS = 30 * 60 * 1000;
const ACTIVATION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function useSessionKeepAlive(enabled: boolean) {
    const requestRunning = useRef(false);
    const lastCheckAt = useRef(0);
    const abortController = useRef<AbortController | null>(null);

    const checkSession = useCallback(async () => {
        const now = Date.now();
        if (!enabled || requestRunning.current || now - lastCheckAt.current < ACTIVATION_CHECK_INTERVAL_MS) return;

        requestRunning.current = true;
        lastCheckAt.current = now;
        const controller = new AbortController();
        abortController.current = controller;

        try {
            await getUserInfo(controller.signal);
        } catch {
            // A 401 is handled by the AuthProvider interceptor. Temporary network
            // errors must not discard an otherwise valid local login state.
        } finally {
            if (abortController.current === controller) abortController.current = null;
            requestRunning.current = false;
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;

        lastCheckAt.current = Date.now();
        const intervalId = window.setInterval(() => checkSession(), KEEP_ALIVE_INTERVAL_MS);
        const checkVisibleSession = () => {
            if (document.visibilityState === "visible") void checkSession();
        };

        document.addEventListener("visibilitychange", checkVisibleSession);
        window.addEventListener("focus", checkVisibleSession);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", checkVisibleSession);
            window.removeEventListener("focus", checkVisibleSession);
            abortController.current?.abort();
            abortController.current = null;
            requestRunning.current = false;
        };
    }, [checkSession, enabled]);
}
